import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Microphone, StopCircle, Spinner } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 60;

/**
 * Accessibility-focused voice-to-text button.
 * Press-and-release flow:
 *   1. Click 🎙️ → asks for mic, starts MediaRecorder (opus/webm)
 *   2. Visual timer counts up; auto-stops at MAX_SECONDS
 *   3. Click ⏹ → stops, posts the audio blob to /api/stt/transcribe
 *   4. Transcribed text is appended to the parent textarea via `onTranscript`
 *
 * The audio file is NEVER saved — transcribe-only. The parent textarea stays
 * fully editable so users can tweak before submitting.
 */
export default function VoiceInput({ onTranscript, label = "Voice input", testIdPrefix = "voice" }) {
  const [state, setState] = useState("idle"); // idle | recording | transcribing | unsupported
  const [elapsed, setElapsed] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Feature-detect — bail out early if the browser can't record.
    if (
      typeof window === "undefined" ||
      !window.MediaRecorder ||
      !navigator?.mediaDevices?.getUserMedia
    ) {
      setState("unsupported");
    }
    return () => cleanup();
  }, []);

  function cleanup() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
  }

  async function start() {
    if (state !== "idle") return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Prefer webm/opus — Whisper accepts it directly.
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recorderRef.current = rec;
      chunksRef.current = [];

      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      rec.onstop = () => uploadAndTranscribe(rec.mimeType || "audio/webm");

      rec.start();
      setState("recording");
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          const next = s + 1;
          if (next >= MAX_SECONDS) stop(); // auto-stop at cap
          return next;
        });
      }, 1000);
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error("Microphone permission denied", {
          description: "Enable mic access in your browser settings to use voice input.",
        });
      } else {
        toast.error("Couldn't access your mic", { description: err?.message || "Unknown error" });
      }
      cleanup();
      setState("idle");
    }
  }

  function stop() {
    if (!recorderRef.current || state !== "recording") return;
    try { recorderRef.current.stop(); } catch { /* ignore */ }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setState("transcribing");
  }

  async function uploadAndTranscribe(mime) {
    const blob = new Blob(chunksRef.current, { type: mime });
    cleanup();

    if (blob.size === 0) {
      toast.error("Nothing was recorded.");
      setState("idle");
      return;
    }

    const form = new FormData();
    const ext = mime.includes("webm") ? "webm" : mime.includes("mp4") ? "mp4" : "webm";
    form.append("file", blob, `voice.${ext}`);

    try {
      const res = await axios.post(`${BACKEND}/api/stt/transcribe`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      const text = (res?.data?.text || "").trim();
      if (!text) {
        toast("Couldn't catch that — try again", {
          description: "Speak a bit louder or longer, then stop the recording.",
        });
      } else {
        onTranscript?.(text);
        toast.success("Transcribed!");
      }
    } catch (err) {
      toast.error("Transcription failed", {
        description: err?.response?.data?.detail || err?.message || "Try typing instead.",
      });
    } finally {
      setState("idle");
    }
  }

  if (state === "unsupported") {
    return (
      <div
        data-testid={`${testIdPrefix}-unsupported`}
        className="text-[11px] text-muted-foreground italic"
      >
        Voice input isn't supported in this browser.
      </div>
    );
  }

  if (state === "transcribing") {
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-transcribing`}
        disabled
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-sm px-3 py-2"
      >
        <Spinner size={14} className="animate-spin" />
        Transcribing…
      </button>
    );
  }

  if (state === "recording") {
    return (
      <button
        type="button"
        data-testid={`${testIdPrefix}-stop`}
        onClick={stop}
        aria-label="Stop recording"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest bg-[#C84A31] text-white border-2 border-[#C84A31] rounded-sm px-3 py-2 hover:bg-[#A63D28] transition-colors animate-pulse"
      >
        <StopCircle size={16} weight="fill" />
        <span className="font-display">Stop · {elapsed}s / {MAX_SECONDS}s</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid={`${testIdPrefix}-start`}
      onClick={start}
      aria-label={label}
      title="Record your message — great for accessibility"
      className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/70 border border-border rounded-sm px-3 py-2 hover:border-[#C84A31] hover:text-[#C84A31] transition-colors"
    >
      <Microphone size={16} weight="duotone" />
      <span className="font-display">Speak it instead</span>
    </button>
  );
}
